import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker, useNavigate } from "react-router-dom";
import axios from "axios";

import apiRoutes from "../constants/api-routes";
import messageLevels from "../constants/message-levels";
import messages from "../constants/messages";
import pages from "../constants/pages";
import useAuthHeader from "../hooks/useAuthHeader";
import useCoAppNavigation from "../hooks/useCoAppNavigation";
import useToastAlert from "../hooks/useToastAlert";
import { selectOrganizationId, selectUser, setOrganizationIdentityProvider, setOrganizationSsoAndScim } from "../redux/userSlice";

export default function useScimWizard() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const currentUser = useSelector(selectUser);
	const organizationId = useSelector(selectOrganizationId);
	const { handleToastAlert } = useToastAlert();
	const { openScimWizard } = useCoAppNavigation();
	const authHeader = useAuthHeader();

	const [activeStep, setActiveStep] = useState(0);
	const [stepsCompleted, setStepsCompleted] = useState({});
	const [inUseDomains, setInUseDomains] = useState([]);
	const [domainError, setDomainError] = useState("");
	const [companyDomains, setCompanyDomains] = useState([]);
	const [identityProviderName, setIdentityProviderName] = useState("Azure");
	const [tenantDetails, setTenantDetails] = useState({});
	const [samlClaims, setSamlClaims] = useState({ email: "", surname: "", givenName: "" });
	const [samlClaimsError, setSamlClaimsError] = useState({ email: "", surname: "", givenName: "" });
	const [metadataUrl, setMetadataUrl] = useState("");
	const [metadataError, setMetadataError] = useState("");
	const [mappedRoles, setMappedRoles] = useState({});
	const [mappedGroups, setMappedGroups] = useState({});
	const [externalGroupsDialogOpen, setExternalGroupsDialogOpen] = useState(false);
	const [externalGroupsFound, setExternalGroupsFound] = useState(false);
	const [externalGroupsTryAgain, setExternalGroupsTryAgain] = useState(false);
	const [noRolesMappedOpen, setNoRolesMappedOpen] = useState(false);
	const [superAdminNotMapped, setSuperAdminNotMapped] = useState(true);
	const [roleMappingStatus, setRoleMappingStatus] = useState({});
	const [roleValidationLoading, setRoleValidationLoading] = useState(false);

	// Blocker to prevent user from leaving the page when configuration is not complete
	let blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			companyDomains.length > 0 && activeStep !== 6 &&
			currentLocation.pathname !== nextLocation.pathname
	);

	const getSteps = () => {
		return [
			{ "num": 1, "desc": "Select IdP" },
			{ "num": 2, "desc": "Configure SSO" },
			{ "num": 3, "desc": "Enter SAML Claims" },
			{ "num": 4, "desc": "Configure SCIM" },
			{ "num": 5, "desc": "Map Roles" },
			{ "num": 6, "desc": "Map Groups (optional)" },
			{ "num": 7, "desc": "Confirm Setup" }
		];
	};

	const handleSubmit = () => {
		switch (activeStep) {
			case 0:	
				if (companyDomains.length === 0) {
					setDomainError("Required *");
				} else {
					handleNext();
				}
				break;
			case 1:
				handleNext();
				break;
			case 2:
				let hasErrors = validateStep3();
				if (!hasErrors) {
					setSamlAppMetadata();
				}
				break;
			case 3:
				handleExternalGroupRetrieval();
				break;
			case 4:
				validateCurrentUserRoleMappings();
				break;
			case 5:
				handleNext();
				break;
			case 6:
				handleConfigurationConfirmed();
				break;
			default:
				break;
		}
	};

	const getContinueButtonText = () => {
		switch (activeStep) {
			case 2:
				return "Save SSO Configuration";
			case 3:
				return "Save SCIM Configuration";
			case 6:
				return "Finish";
			default:
				return "Next";
		}
	};

	const validateStep3 = () => {
		let hasErrors = false;
		let tempErrors = { email: "", givenName: "", surname: "" };
		if (samlClaims.email === "") {
			tempErrors.email = "Required *";
			hasErrors = true;
		}
		if (samlClaims.givenName === "") {
			tempErrors.givenName = "Required *";
			hasErrors = true;
		}
		if (samlClaims.surname === "") {
			tempErrors.surname = "Required *";
			hasErrors = true;
		}
		if (metadataUrl === "") {
			setMetadataError("Required *");
			hasErrors = true;
		}
		if (hasErrors) {
			setSamlClaimsError(tempErrors);
		}
		return hasErrors;
	};

	const validateRoleMappings = (mappings, type) => {
		if (mappings.length === 0) {
			setSuperAdminNotMapped(true);
			return;
		}
		const validateData = {
			mappings: mappings,
			type: type
		};

		axios.post(apiRoutes.validateRoleMappings, validateData, { headers: authHeader })
			.then(({ data }) => {
				if (data.status) {
					setSuperAdminNotMapped(false);
				} else {
					setSuperAdminNotMapped(true);
				}
			})
			.catch(err => {
				console.log(err);
			});
	};

	const validateCurrentUserRoleMappings = () => {
		const validateData = {
			mappings: mappedRoles,
			type: "validateCurrentUserRoleStatus",
			descopeUserId: currentUser.descopeUserId
		};
		setRoleValidationLoading(true);
		axios.post(apiRoutes.validateRoleMappings, validateData, { headers: authHeader })
			.then(({ data }) => {
				if (data.protectedStatus) {
					handleNext();
					setRoleValidationLoading(false);
				} else {
					setNoRolesMappedOpen(true);
					setRoleMappingStatus(data);
					setRoleValidationLoading(false);
				}
			})
			.catch(err => {
				console.log(err);
				setRoleValidationLoading(false);
			});
	};

	const handleCloseRoleValidationModal = () => {
		setNoRolesMappedOpen(false);
		setRoleMappingStatus({});
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleExternalGroupRetrieval = () => {
		axios.get(apiRoutes.getExternalGroups(organizationId), { headers: authHeader })
			.then((res) => {
				if (res.data.length > 0 && !externalGroupsDialogOpen) {
					handleNext();
				} else if (externalGroupsDialogOpen && res.data.length > 0) {
					setExternalGroupsFound(true);
				} else {
					setExternalGroupsDialogOpen(true);
					setTimeout(() => {
						setExternalGroupsTryAgain(false);
					}, 4000);
				}
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const handleClearCurrentConfiguration = () => {
		axios.delete(apiRoutes.ssoAndScim(currentUser.organizationId), { headers: authHeader })
			.then(() => {
				blocker.proceed();
				dispatch(setOrganizationSsoAndScim(false));
				dispatch(setOrganizationIdentityProvider(""));
			})
			.catch(err => console.error(err));
	};

	const handleConfigurationConfirmed = () => {
		const groupsToMap = Object.entries(mappedGroups).filter(([_, value]) => value.length > 0);
		const rolesToMap = Object.entries(mappedRoles).filter(([_, value]) => value.length > 0);
		const unmappedGroups = Object.entries(mappedGroups).filter(([_, value]) => value.length === 0);
		const unmappedRoles = Object.entries(mappedRoles).filter(([_, value]) => value.length === 0);
		const postData = {
			mappedGroups: groupsToMap,
			mappedRoles: rolesToMap,
			unmappedGroups: unmappedGroups,
			unmappedRoles: unmappedRoles,
			identityProviderName: identityProviderName,
			domains: companyDomains
		};
		axios.post(apiRoutes.ssoAndScim(currentUser.organizationId), postData, { headers: authHeader })
			.then(() => {
				handleToastAlert(messageLevels.SUCCESS, messages.SSO_AND_SCIM_CONFIGURED_SUCCESS_TOAST_MSG);
				navigate(pages.organizationManagement);
				dispatch(setOrganizationIdentityProvider(identityProviderName));
				dispatch(setOrganizationSsoAndScim(true));
			})
			.catch((err) => {
				handleToastAlert(messageLevels.ERROR, messages.SSO_AND_SCIM_CONFIGURED_ERROR_TOAST_MSG);
				console.log(err);
			});
	};

	/**
	* Step Progression
	*/
	const handleMoveUpOneStep = () => {
		setActiveStep((activeStep) => activeStep + 1);
	};

	const handleNext = async () => {
		setStepsCompleted({ ...stepsCompleted, [`${activeStep}`]: true });
		switch (activeStep) {
			case 0:
				handleMoveUpOneStep();
				break;
			case 1:
				handleMoveUpOneStep();
				break;
			case 2:
				handleMoveUpOneStep();
				break;
			case 3:
				setExternalGroupsDialogOpen(false);
				handleMoveUpOneStep();
				break;
			case 4:
				handleMoveUpOneStep();
				setNoRolesMappedOpen(false);
				break;
			case 5:
				handleMoveUpOneStep();
				break;
			case 6:
				handleMoveUpOneStep();
				break;
			default:
				console.log("unknown case");
				break;
		}
	};

	const setSamlAppMetadata = () => {
		const metadata = {
			tenantId: tenantDetails.tenantId,
			metadataUrl: metadataUrl,
			emailClaimName: samlClaims.email,
			givenNameClaimName: samlClaims.givenName,
			surnameClaimName: samlClaims.surname,
			domains: companyDomains
		};
		axios.post(apiRoutes.setSamlAppMetadata(currentUser.organizationId), metadata, { headers: authHeader })
			.then(() => {
				handleNext();
				handleToastAlert(messageLevels.SUCCESS, messages.SSO_CONFIGURED_SUCCESS_TOAST_MSG);
			})
			.catch(err => {
				handleToastAlert(messageLevels.ERROR, messages.SSO_CONFIGURED_ERROR_TOAST_MSG);
				console.log(err);
			});
	};

	const initOrganization = () => {
		axios.get(apiRoutes.getOrganization(currentUser.organizationId), {
			headers: authHeader
		})
			.then((res) => {
				setTenantDetails({ secretScimKey: res.data.secretScimKey, tenantId: res.data.descopeTenantId, entityId: res.data.ssoMetadata.tenantEntityId, acsUrl: res.data.ssoMetadata.tenantAcsUrl });
				setInUseDomains(res.data.inUseDomains);
			})
			.catch(err => console.error(err));
	};

	/**
	 * Stops user when refreshing the page. And prompts user with browser alert.
	 * Note: Fired before the content is unloaded.
	 */
	const alertUser = (e) => {
		e.preventDefault();
	};

	/**
	 * Deletes the current configuration when the user chooses to refresh the page.
	 * Note: Fired after the content is unloaded or when a user chooses to leave the page.
	 */
	const deleteConfiguration = (e) => {
		if (e.type === "unload") {
			e.preventDefault();
			handleClearCurrentConfiguration();
		}
		if (blocker.state === "blocked") {
			handleClearCurrentConfiguration();
			blocker.proceed();
		}
	};

	const handleCancelExit = () => {
		openScimWizard(false);
		blocker.reset();
	};

	return {
		// State
		activeStep,
		organizationId,
		stepsCompleted,
		noRolesMappedOpen,
		superAdminNotMapped,
		roleMappingStatus,
		roleValidationLoading,

		// State + Setters
		domainError, setDomainError,
		companyDomains, setCompanyDomains,
		identityProviderName, setIdentityProviderName,
		inUseDomains, setInUseDomains,
		tenantDetails, setTenantDetails,
		samlClaims, setSamlClaims,
		samlClaimsError, setSamlClaimsError,
		metadataUrl, setMetadataUrl,
		metadataError, setMetadataError,
		mappedRoles, setMappedRoles,
		mappedGroups, setMappedGroups,
		externalGroupsDialogOpen, setExternalGroupsDialogOpen,
		externalGroupsFound, setExternalGroupsFound,
		externalGroupsTryAgain, setExternalGroupsTryAgain,

		// State Handlers
		getSteps, handleSubmit,
		getContinueButtonText, validateStep3,
		validateRoleMappings, validateCurrentUserRoleMappings,
		handleCloseRoleValidationModal, handleBack,
		handleExternalGroupRetrieval, handleClearCurrentConfiguration,
		handleConfigurationConfirmed, handleMoveUpOneStep,
		handleNext, setSamlAppMetadata,
		initOrganization, alertUser,
		deleteConfiguration, handleCancelExit,

		//Blocker used to prevent user from leaving the page when configuration is not complete
		blocker
	};
}